import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Heart, Truck, Gem, ChevronLeft, ChevronRight, ShieldCheck, Camera, X } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import SEO from "@/components/SEO";
import { LOGO_URL, HERO_IMAGE, CATEGORIES, CATEGORY_IMAGES, BRAND } from "@/lib/brand";
import ProductCard from "@/components/ProductCard";
import MoonAvatar from "@/components/MoonAvatar";
import CustomerReviews from "@/components/CustomerReviews";

/** Stacked overlapping carousel for "Styled by Moon".
 *  - Large centered image, adjacent slides peek on either side (smaller + faded).
 *  - Auto-advances every 6s; pauses for 8s after any manual swipe/drag.
 *  - Pointer-drag / touch-swipe to navigate.
 *  - Clicking the CENTER card behaves exactly like before (link to product/style page).
 */

/**
 * ShopLookModal — appears when the customer clicks "Shop This Look" on a Styled by Moon
 * look that has MULTIPLE tagged products. Shows small thumbnails of each tagged product;
 * clicking one navigates straight to that product page.
 */
function ShopLookModal({ look, products, onClose }) {
  const tagged = (look.product_ids || [])
    .map((pid) => products.find((p) => p.id === pid))
    .filter(Boolean);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="shop-look-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between p-5 border-b border-sj-ink/10">
          <div>
            <div className="overline text-sj-goldDark">Shop this look</div>
            <div className="mt-1 font-display text-lg text-sj-ink line-clamp-1">{look.label}</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-sj-cream" aria-label="Close" data-testid="shop-look-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-5">
          {tagged.length === 0 ? (
            <p className="text-sm text-sj-muted text-center py-6">No tagged products available.</p>
          ) : (
            <ul className="space-y-3">
              {tagged.map((p) => (
                <li key={p.id}>
                  <Link
                    to={`/product/${p.id}`}
                    onClick={onClose}
                    className="flex items-center gap-4 p-2 rounded-md hover:bg-sj-cream transition-colors group"
                    data-testid={`shop-look-item-${p.id}`}
                  >
                    <div className="h-16 w-16 rounded-md overflow-hidden bg-white border border-sj-ink/10 shrink-0">
                      {p.images?.[0] ? (
                        <img src={resolveImage(p.images[0], { w: 200 })} alt={p.name} className="w-full h-full object-contain" loading="lazy" />
                      ) : null}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-sj-ink truncate group-hover:text-sj-goldDark transition-colors">{p.name}</div>
                      <div className="mt-0.5 text-[11px] uppercase tracking-wider text-sj-inkSoft">{p.category}</div>
                      <div className="mt-0.5 text-sm font-medium text-sj-ink">₹{Number(p.price).toLocaleString("en-IN")}</div>
                    </div>
                    <ArrowRight size={16} className="text-sj-inkSoft group-hover:text-sj-goldDark shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}


function StyledCarousel({ looks, onShopLook }) {
  const [idx, setIdx] = useState(0);
  const total = looks.length;
  const pauseUntil = useRef(0);
  const dragStart = useRef(null);
  const dragged = useRef(false);
  // Refs to the <video> element for each slide index — lets the carousel
  // start/stop playback in response to which slide is centered.
  const videoRefs = useRef({});

  // Photo slides advance every 6s; video slides advance on the video's own
  // `ended` event. We only run the timer while the active slide is a photo.
  const activeIsVideo = !!(looks[idx] && looks[idx].video);
  useEffect(() => {
    if (total < 2) return;
    if (activeIsVideo) return; // duration-driven; timer paused
    const id = setInterval(() => {
      if (Date.now() < pauseUntil.current) return;
      setIdx((v) => (v + 1) % total);
    }, 6000);
    return () => clearInterval(id);
  }, [total, activeIsVideo, idx]);

  // Whenever the active slide changes, pause every other slide's video and
  // (re)start the active one from t=0. Peeking (side) slides never play.
  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([key, el]) => {
      if (!el) return;
      if (Number(key) === idx) {
        try {
          el.currentTime = 0;
          const p = el.play();
          if (p && typeof p.catch === "function") p.catch(() => {}); // ignore autoplay rejections
        } catch (e) { /* noop */ }
      } else {
        try { el.pause(); } catch (e) { /* noop */ }
      }
    });
  }, [idx]);

  if (total === 0) return null;

  const bump = (dir) => {
    pauseUntil.current = Date.now() + 8000;
    setIdx((v) => (v + dir + total) % total);
  };

  const onPointerDown = (e) => { dragStart.current = e.clientX; dragged.current = false; };
  const onPointerMove = (e) => {
    if (dragStart.current == null) return;
    if (Math.abs(e.clientX - dragStart.current) > 8) dragged.current = true;
  };
  const onPointerUp = (e) => {
    if (dragStart.current == null) return;
    const dx = e.clientX - dragStart.current;
    dragStart.current = null;
    if (Math.abs(dx) > 50) bump(dx < 0 ? +1 : -1);
  };

  // Compute offsets for slides relative to center (position = signed distance)
  const positionFor = (i) => {
    let p = i - idx;
    if (p > total / 2) p -= total;
    if (p < -total / 2) p += total;
    return p;
  };

  return (
    <div
      className="relative w-full select-none touch-pan-y"
      style={{ perspective: 1200 }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => { dragStart.current = null; }}
      data-testid="styled-carousel"
    >
      {/* Stage — sized to fit 9:16 cards (420 × 747px at scale=1). */}
      <div className="relative mx-auto h-[560px] sm:h-[720px] lg:h-[800px] max-w-[1300px]">
        {looks.map((s, i) => {
          const p = positionFor(i);
          const absp = Math.abs(p);
          if (absp > 2) return null; // only render up to 2 slides on each side

          const productIds = Array.isArray(s.product_ids) ? s.product_ids.filter(Boolean) : [];
          const singleHref = productIds.length === 1 ? `/product/${productIds[0]}` : null;

          // Center card: full opacity, scale 1, z-high. Neighbors: smaller, faded, offset.
          const isCenter = p === 0;
          const translateX = p * 34; // % of container width
          const scale = isCenter ? 1 : (absp === 1 ? 0.82 : 0.66);
          const opacity = isCenter ? 1 : (absp === 1 ? 0.85 : 0.45);
          const rotateY = p * -8; // subtle 3D tilt
          const z = 30 - absp * 10;

          const isVideoSlide = !!s.video;
          const mediaElement = isVideoSlide ? (
            <video
              ref={(el) => { videoRefs.current[i] = el; }}
              src={`/api/videos/${s.video}`}
              className="w-full h-full object-cover bg-black"
              muted
              playsInline
              // Aggressive preload for center + immediate neighbours so playback
              // starts instantly the moment they become the active slide.
              preload={absp <= 1 ? "auto" : "metadata"}
              poster={s.image ? resolveImage(s.image, { w: 800 }) : undefined}
              // autoPlay is toggled programmatically via play()/pause() in the
              // useEffect above so peeking slides never start playing.
              onEnded={() => {
                // Only advance if THIS slide is still the active one and we're
                // not inside a user-initiated pause window.
                if (looks[idx] && looks[idx].id === s.id && Date.now() >= pauseUntil.current) {
                  setIdx((v) => (v + 1) % total);
                }
              }}
              data-testid={`style-look-video-${s.id}`}
            />
          ) : (
            <img
              src={resolveImage(s.image, { w: 1400 })}
              alt={s.label}
              className="w-full h-full object-cover"
              draggable={false}
              loading={isCenter ? "eager" : "lazy"}
              decoding="async"
            />
          );

          const commonBody = (
            <>
              {mediaElement}
              {isCenter && (
                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 bg-gradient-to-t from-black/65 to-transparent flex items-end justify-between gap-3">
                  <span className="text-white text-base sm:text-lg tracking-wide font-medium">{s.label}</span>
                  {productIds.length >= 1 && (
                    <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium bg-white/95 text-sj-ink px-3 py-2 rounded-full whitespace-nowrap">
                      Shop This Look <ArrowRight size={13} />
                    </span>
                  )}
                </div>
              )}
            </>
          );

          const commonClass = `absolute top-1/2 left-1/2 block rounded-2xl overflow-hidden shadow-2xl transition-all duration-500 ease-out ${isCenter ? "ring-1 ring-sj-gold/40" : ""}`;
          // 9:16 portrait — width capped, height derived so videos display in
          // full without cropping.
          const cardWidth = "min(72vw, 420px)";
          const commonStyle = {
            transform: `translate(-50%, -50%) translateX(${translateX}%) scale(${scale}) rotateY(${rotateY}deg)`,
            width: cardWidth,
            height: `calc(${cardWidth} * 16 / 9)`,
            opacity,
            zIndex: z,
          };

          // If exactly ONE product: use Link (direct navigation, same as before).
          // If MULTIPLE or ZERO products: use button that opens the Shop-this-look picker.
          if (singleHref) {
            return (
              <Link
                key={s.id}
                to={singleHref}
                onClick={(e) => {
                  if (dragged.current) { e.preventDefault(); return; }
                  if (!isCenter) { e.preventDefault(); pauseUntil.current = Date.now() + 8000; setIdx(i); }
                }}
                aria-label={s.label}
                data-testid={`style-look-${s.id}`}
                className={commonClass}
                style={commonStyle}
              >
                {commonBody}
              </Link>
            );
          }
          return (
            <button
              key={s.id}
              type="button"
              onClick={(e) => {
                if (dragged.current) return;
                if (!isCenter) { pauseUntil.current = Date.now() + 8000; setIdx(i); return; }
                if (productIds.length === 0) { window.location.href = "/style"; return; }
                pauseUntil.current = Date.now() + 20000;
                onShopLook && onShopLook(s);
              }}
              aria-label={s.label}
              data-testid={`style-look-${s.id}`}
              className={commonClass}
              style={commonStyle}
            >
              {commonBody}
            </button>
          );
        })}
      </div>

      {/* Dots */}
      {total > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6" data-testid="styled-carousel-dots">
          {looks.map((_, i) => (
            <button
              key={i}
              onClick={() => { pauseUntil.current = Date.now() + 8000; setIdx(i); }}
              aria-label={`Slide ${i + 1}`}
              data-testid={`styled-carousel-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-sj-gold" : "w-3 bg-sj-ink/20 hover:bg-sj-ink/40"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}


function RotatingBanner({ slots, mobileSlots, links }) {
  const pairs = (slots || []).map((s, i) => ({
    img: s,
    mobileImg: (mobileSlots || [])[i] || null,
    link: (links || [])[i] || null,
  })).filter((p) => p.img || p.mobileImg);
  const images = pairs.map((p) => p.img || p.mobileImg);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (images.length < 2) return;
    const id = setInterval(() => setI((v) => (v + 1) % images.length), 7000);
    return () => clearInterval(id);
  }, [images.length]);

  // ⚠️ Do NOT return null while /api/banners is still loading. Render the
  // hero placeholder immediately using the STABLE preload URL so the browser
  // consumes the <link rel="preload"> response and paints instantly.
  //
  // Key insight: keep the FIRST <img> element in the DOM identical whether the
  // API has responded or not. React reconciles the same node, no unmount, no
  // duplicate network request. `slotZero` is the URL for slide 0.
  const hasApiSlides = images.length > 0;
  const slotZeroDesktop = "/api/banners/hero-desktop.jpg";
  const slotZeroMobile = "/api/banners/hero-mobile.jpg";
  const activeIdx = hasApiSlides ? i : 0;

  const prev = () => setI((v) => (v - 1 + images.length) % images.length);
  const next = () => setI((v) => (v + 1) % images.length);

  return (
    <section className="relative w-full overflow-hidden bg-sj-cream" data-testid="home-banner">
      {/* Universal banner — mobile taller (4:5 portrait) for more visual impact; desktop 3:1 */}
      <div className="relative w-full aspect-[4/5] md:aspect-[3/1]">
        {/* Slot 0 is ALWAYS the stable hero URL (matches <link rel="preload">).
            Rendered whether or not /api/banners has resolved yet — the DOM node
            stays identical so React never unmounts / re-fetches it. */}
        {(() => {
          const link0 = hasApiSlides ? (pairs[0]?.link || null) : null;
          const img0 = (
            <picture>
              <source media="(min-width: 768px)" srcSet={slotZeroDesktop} />
              <img
                src={slotZeroMobile}
                alt=""
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${activeIdx === 0 ? "opacity-100 z-[1]" : "opacity-0 z-0"}`}
                data-testid="banner-slide-0"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </picture>
          );
          return link0 ? (
            <Link
              to={link0}
              className={`absolute inset-0 ${activeIdx === 0 ? "pointer-events-auto" : "pointer-events-none"}`}
              aria-label="Open banner destination"
              data-testid="banner-link-0"
            >
              {img0}
            </Link>
          ) : (
            <div className={`absolute inset-0 ${activeIdx === 0 ? "" : "pointer-events-none"}`}>{img0}</div>
          );
        })()}

        {/* Remaining slides — appear only after /api/banners has resolved and returned >1 banners. */}
        {hasApiSlides && pairs.slice(1).map((p, idx0) => {
          const idx = idx0 + 1;
          const desktopSrc = p.img || p.mobileImg;
          const mobileSrc = p.mobileImg || p.img;
          const img = (
            <picture>
              <source media="(min-width: 768px)" srcSet={resolveImage(desktopSrc, { w: 2400 })} />
              <img
                src={resolveImage(mobileSrc, { w: 1200 })}
                alt=""
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${idx === i ? "opacity-100 z-[1]" : "opacity-0 z-0"}`}
                data-testid={`banner-slide-${idx}`}
                loading="lazy"
                fetchPriority="auto"
                decoding="async"
              />
            </picture>
          );
          return p.link ? (
            <Link
              key={idx}
              to={p.link}
              className={`absolute inset-0 ${idx === i ? "pointer-events-auto" : "pointer-events-none"}`}
              aria-label="Open banner destination"
              data-testid={`banner-link-${idx}`}
            >
              {img}
            </Link>
          ) : (
            <div key={idx} className="absolute inset-0 pointer-events-none">
              {img}
            </div>
          );
        })}
        {images.length > 1 && (
          <>
            <button onClick={prev} aria-label="Previous" data-testid="banner-prev" className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 hover:bg-white backdrop-blur items-center justify-center z-[2]">
              <ChevronLeft size={16} />
            </button>
            <button onClick={next} aria-label="Next" data-testid="banner-next" className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 hover:bg-white backdrop-blur items-center justify-center z-[2]">
              <ChevronRight size={16} />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-[2]">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setI(idx)}
                  data-testid={`banner-dot-${idx}`}
                  aria-label={`Slide ${idx + 1}`}
                  className={`h-1.5 rounded-full transition-all ${idx === i ? "w-8 bg-sj-gold" : "w-3 bg-white/70 hover:bg-white"}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [collections, setCollections] = useState([]);
  const [banners, setBanners] = useState([]);
  const [bannerLinks, setBannerLinks] = useState([]);
  const [mobileBanners, setMobileBanners] = useState([]);
  const [siteImages, setSiteImages] = useState({});
  const [showAllCollections, setShowAllCollections] = useState(false);
  const [occasions, setOccasions] = useState([]);
  const [styleLooks, setStyleLooks] = useState([]);
  const [shopLookOpen, setShopLookOpen] = useState(null); // the style-look whose product picker is open
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setAllProducts(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/products", { params: { featured: true } })
      .then(({ data }) => setFeatured(data.slice(0, 8)))
      .catch(() => {});
    api.get("/collections").then(({ data }) => setCollections(data)).catch(() => {});
    api.get("/banners").then(({ data }) => {
      setBanners(data.slots || []);
      setBannerLinks(data.links || []);
      setMobileBanners(data.mobile_slots || []);
    }).catch(() => {});
    api.get("/site-images").then(({ data }) => setSiteImages(data || {})).catch(() => {});
    api.get("/occasions").then(({ data }) => setOccasions(data || [])).catch(() => {});
    api.get("/style-looks").then(({ data }) => setStyleLooks(data || [])).catch(() => {});
  }, []);

  const heroImg = siteImages.hero ? resolveImage(siteImages.hero) : HERO_IMAGE;

  return (
    <main data-testid="home-page" className="flex flex-col">
      <SEO
        title="Star Jewels — Premium Artificial & Fashion Jewellery"
        description="Shop hand-crafted artificial jewelry — earrings, necklaces, bracelets and rings — made in India. Free shipping over ₹1,499. Meet Moon, our AI stylist."
        path="/"
      />
      {/* ROTATING BANNER (auto-changes every 7s, admin-managed) */}
      <div style={{order: 1}}><RotatingBanner slots={banners} mobileSlots={mobileBanners} links={bannerLinks} /></div>

      {/* Order — CSS: 1 Hero banner, 2 Hero text, 3 Featured/Best Sellers, 4 Occasions, 5 Collections, 6 Why Star Jewels, 7 Styled by Moon, 8 Reviews, 9 Newsletter (just above footer) */}

      {/* HERO — text-only, centered, no photo */}
      <section style={{order: 2}} className="relative overflow-hidden bg-sj-cream">
        <div className="mx-auto max-w-4xl px-5 sm:px-10 py-10 sm:py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
            className="overline mb-4">Est. 2026 · Fine Fashion Jewellery</motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.1 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight text-sj-ink"
            data-testid="hero-title">
            A little <span className="gold-text font-medium">starlight </span>
            for every day.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-6 sm:mt-7 text-sj-inkSoft max-w-2xl mx-auto leading-relaxed text-base sm:text-lg lg:text-xl">
              Hand-crafted, gold-toned pieces designed to layer, stack and shine — earrings, necklaces, bracelets and rings.
          </motion.p>
        </div>
      </section>

      {/* SECTION A: SHOP BY OCCASION — below hero text, above Shop by Collection */}
      {occasions.length > 0 && (
        <section style={{order: 4}} className="mx-auto max-w-[1440px] w-full px-5 sm:px-10 py-10 sm:py-16" data-testid="home-occasions">
          <div className="text-center mb-8 sm:mb-12">
            <div className="overline">Curated for the moment</div>
            <h2 className="font-display text-3xl sm:text-4xl font-light mt-2">Shop by Occasion</h2>
            <p className="mt-3 text-sj-inkSoft max-w-lg mx-auto text-sm sm:text-base">
              Find the perfect jewellery for every celebration and special moment.
            </p>
          </div>
          {/* Mobile: single-column stack, full-width cards.
              Desktop (md+): flex row with cards stretching equally via flex-1. */}
          <div className="flex flex-col md:flex-row gap-4 sm:gap-6 w-full">
            {occasions.map((o, i) => (
              <motion.div
                key={o.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.06 }}
                data-testid={`occasion-card-${o.id}`}
                className="w-full md:flex-1 md:w-auto md:min-w-0"
              >
                <Link
                  to={o.link || "/shop"}
                  className="occasion-card block overflow-hidden rounded-lg group shadow-sm hover:shadow-xl transition-shadow duration-500 bg-white"
                >
                  {/* Image / placeholder — separate box, never overlaid by text */}
                  <div className="aspect-square overflow-hidden bg-sj-cream flex items-center justify-center">
                    {o.image ? (
                      <img
                        src={resolveImage(o.image, { w: 700 })}
                        alt={o.title}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="text-sj-muted text-xs tracking-wider uppercase" data-testid={`occasion-noimg-${o.id}`}>No image yet</div>
                    )}
                  </div>
                  {/* Text — stacked cleanly below the image, each with its own row */}
                  <div className="p-4 sm:p-5 text-center">
                    <div className="font-display text-lg sm:text-xl leading-tight text-sj-ink">{o.title}</div>
                    {o.subtitle && (
                      <div className="mt-1.5 text-xs sm:text-sm text-sj-inkSoft">{o.subtitle}</div>
                    )}
                    <div className="mt-3 inline-flex items-center gap-1 text-[11px] sm:text-xs font-medium text-sj-goldDark tracking-wider uppercase group-hover:translate-x-1 transition-transform">
                      Shop Now <ArrowRight size={13} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* COLLECTIONS — 4 tiles + View all toggle */}
      <section style={{order: 5}} className="mx-auto max-w-[1440px] px-5 sm:px-10 py-10 sm:py-16" data-testid="home-collections">
        <div className="text-center mb-8 sm:mb-12">
          <div className="overline">Curated collections</div>
          <h2 className="font-display text-3xl sm:text-4xl font-light mt-2">Shop by collection</h2>
          {collections.length > 4 && (
            <button
              onClick={() => setShowAllCollections((v) => !v)}
              className="inline-flex items-center gap-1 text-sm text-sj-inkSoft hover:text-sj-goldDark mt-4"
              data-testid="collections-view-all"
            >
              {showAllCollections ? "Show less" : `View all (${collections.length})`}
              <ArrowRight size={14} className={showAllCollections ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>
          )}
        </div>

        {collections.length === 0 ? (
          <div className="text-center text-sj-muted text-sm py-8">Collections coming soon.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="collections-grid">
            {(showAllCollections ? collections : collections.slice(0, 4)).map((c, i) => {
              const assignedFeatured = featured.find((p) => (p.collection_ids || []).includes(c.id));
              const fallbacks = Object.values(CATEGORY_IMAGES);
              const preview = c.cover_image
                ? resolveImage(c.cover_image, { w: 700 })
                : (assignedFeatured?.images?.[0]
                    ? resolveImage(assignedFeatured.images[0], { w: 700 })
                    : fallbacks[i % fallbacks.length]);
              return (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: (i % 4) * 0.05 }}
                  data-testid={`collection-tile-${c.id}`}
                >
                  <Link to={`/shop?collection=${c.id}`} className="cat-tile block group">
                    <div className="aspect-square overflow-hidden bg-sj-cream">
                      <img src={preview} alt={c.name} className="w-full h-full object-contain" loading="lazy" decoding="async" width="700" height="700" />
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 z-10 flex items-center justify-between">
                      <span className="font-display text-white text-lg tracking-wide leading-tight">{c.name}</span>
                      <ArrowRight size={16} className="text-white opacity-80 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>

      <div style={{order: 6.5}} className="gold-divider mx-auto max-w-3xl" />

      {/* FEATURED */}
      <section style={{order: 3}} className="mx-auto max-w-[1440px] px-5 sm:px-10 py-10 sm:py-16" data-testid="home-featured">
        <div className="text-center mb-8 sm:mb-12">
          <div className="overline">Handpicked for you</div>
          <h2 className="font-display text-3xl sm:text-4xl font-light mt-2">Featured pieces</h2>
          <p className="mt-3 text-sj-inkSoft max-w-lg mx-auto text-sm">
            Every piece is chosen for the way it feels — warm to the touch, easy to wear, thoughtful in detail.
          </p>
        </div>

        {featured.length === 0 ? (
          <div className="text-center text-sj-muted text-sm">Loading pieces…</div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} hideFeaturedBadge />)}
          </div>
        )}

        <div className="text-center mt-8 sm:mt-12">
          <Link to="/shop" className="btn-primary" data-testid="featured-view-all">View entire collection <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* SECTION B: STYLED BY MOON — stacked overlapping carousel (hidden if no real images) */}
      {styleLooks.filter((s) => s.image || s.video).length > 0 && (
      <section style={{order: 7}} className="mx-auto max-w-[1440px] px-5 sm:px-10 py-10 sm:py-16" data-testid="home-style">
          <div className="text-center mb-8 sm:mb-12">
            <div className="overline">Style inspiration</div>
            <div className="mt-2 flex items-center justify-center gap-3">
              <MoonAvatar size={40} />
              <h2 className="font-display text-3xl sm:text-4xl font-light">Styled by Moon</h2>
            </div>
            <p className="mt-3 text-sj-inkSoft italic text-sm sm:text-base">
              Curated styling ideas by Moon.
            </p>
          </div>
          <StyledCarousel
            looks={styleLooks.filter((s) => s.image || s.video).slice(0, 10)}
            onShopLook={(look) => setShopLookOpen(look)}
          />
          <div className="text-center mt-8 sm:mt-10">
            <Link to="/style" className="btn-outline" data-testid="style-explore-more">
              Explore More Looks <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      )}

      {/* WHY STAR JEWELS */}

      <section style={{order: 6}} className="mx-auto max-w-[1440px] px-5 sm:px-10 py-10 sm:py-16" data-testid="home-why">
        <div className="text-center mb-8 sm:mb-12">
          <div className="overline">Why Star Jewels</div>
          <h2 className="font-display text-3xl sm:text-4xl font-light mt-2">Crafted for you, quietly extraordinary</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
        {[
          { icon: <Sparkles size={22} strokeWidth={1.4} />,    t: "Skin-friendly finishes", s: "Gentle plating, kind to sensitive skin" },
          { icon: <ShieldCheck size={22} strokeWidth={1.4} />, t: "Secure Razorpay Checkout", s: "Encrypted payments, trusted gateway" },
          { icon: <Truck size={22} strokeWidth={1.4} />,       t: "Ships Across India", s: "Careful, tracked delivery, pan-India" },
          { icon: <Camera size={22} strokeWidth={1.4} />,      t: "Real Product Photography", s: "What you see is what you receive" },
        ].map((it, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.06 }}
            className="p-5 text-center"
            data-testid={`why-badge-${i}`}
          >
            <div className="mx-auto w-12 h-12 rounded-full bg-sj-cream flex items-center justify-center text-sj-goldDark">
              {it.icon}
            </div>
            <div className="font-display text-base mt-4">{it.t}</div>
            <div className="text-xs text-sj-muted mt-1">{it.s}</div>
          </motion.div>
        ))}
        </div>
      </section>

      {/* NEWSLETTER — last section, flush against footer (both bg-sj-ink) */}
      <section style={{order: 9}} className="bg-sj-ink text-white" data-testid="home-newsletter">
        <div className="mx-auto max-w-2xl px-5 sm:px-10 py-12 sm:py-16 text-center">
          <div className="overline text-sj-gold">Star List</div>
          <h2 className="font-display text-3xl sm:text-4xl font-light mt-3">Be the first to shine.</h2>
          <p className="mt-3 text-white/75 text-sm sm:text-base">Join our list for new launches, quiet restocks and offers we only share here.</p>
          <form onSubmit={(e) => { e.preventDefault(); const email = e.target.querySelector('input[type=email]').value; if (!email) return; import("sonner").then(({ toast }) => toast.success("You're on the list — thank you.")); e.target.reset(); }} className="mt-6 flex gap-2 max-w-md mx-auto" data-testid="newsletter-form">
            <input type="email" required placeholder="your@email.com" className="flex-1 h-11 rounded-full px-4 text-sm bg-white text-sj-ink outline-none" data-testid="newsletter-input" />
            <button type="submit" className="h-11 px-5 rounded-full bg-sj-gold text-sj-ink text-sm font-medium hover:brightness-110" data-testid="newsletter-submit">Sign up</button>
          </form>
        </div>
      </section>

      {/* Reviews — CSS order 9 places this last, just before the footer */}
      <CustomerReviews />

      {/* Shop this look picker modal — appears when a Styled by Moon look has >1 tagged product */}
      {shopLookOpen && (
        <ShopLookModal
          look={shopLookOpen}
          products={allProducts}
          onClose={() => setShopLookOpen(null)}
        />
      )}
    </main>
  );
}
